/**
 * Logs the user out when earthnode stops recognising their session.
 *
 * earthnode answers every request made with an expired JWT with HTTP 200 and
 * `{ Success: false, ErrorCode: 1000, ErrorMessage: 'Session expired. Please
 * reauthenticate and try again.' }`. Nothing acted on that, so each hook threw
 * the message — usually as an unhandled rejection — and left the user on a
 * dashboard whose every request now fails. `api.config.ts` calls
 * `installSessionExpiryDetection` on the API object; ClientLogin.provider registers the
 * handler that performs the logout.
 */

import { parseJWT, type Api } from 'vopay';

/**
 * `VIOAPIErrorCodes::AUTH_FAILED` — expired JWT, invalid JWT, bad credentials,
 * but also business-rule failures on live sessions (e.g. bill-pay's country
 * allowlist, SingleUseToken flows). The code alone therefore doesn't prove the
 * session is gone — the handler confirms with `isJwtExpired` before logging out.
 */
const AUTH_FAILED_ERROR_CODE = 1000;

/** Returns whether the expiry was taken over, i.e. the user is being logged out. */
type SessionExpiredHandler = () => boolean;

let isSessionExpired: SessionExpiredHandler | null = null;

export function setSessionExpiredHandler(handler: SessionExpiredHandler | null): void {
  isSessionExpired = handler;
}

/**
 * Makes every API method report an unauthenticated session to the registered
 * handler, instead of leaving each of the ~300 hooks to throw `ErrorMessage` at a
 * user who can no longer do anything on the page.
 *
 * The methods are patched in place instead of being wrapped in a new object
 * because `vp.VoPayApi` hands this same object to `withApiHook`, which is how
 * most of the dashboard's hooks receive their `api` — a copy would miss them.
 *
 * Once a logout is under way each swallowed request is answered with the shared
 * `NEVER_SETTLES` promise, so the caller's
 * `if (!response.Success) throw new Error(response.ErrorMessage)` never runs —
 * that is what surfaced "Session expired. Please reauthenticate and try again."
 * as an unhandled rejection.
 */

/**
 * A single promise that never settles, shared by every swallowed request so a
 * logout doesn't allocate one per intercepted call (~300 API methods). Its
 * pending `.then` chain becomes unreachable — and GC-eligible — once the logout
 * navigation unmounts the components awaiting it.
 */
const NEVER_SETTLES: Promise<never> = new Promise(() => {});

export function installSessionExpiryDetection(api: Api): void {
  const apiMethods: Record<string, unknown> = api;

  Object.entries(apiMethods).forEach(([methodName, method]) => {
    if (typeof method !== 'function') return;

    apiMethods[methodName] = (...args: unknown[]) => {
      const result: unknown = method(...args);

      if (!(result instanceof Promise)) return result;

      return result.then((response: unknown) => {
        if (!isAuthFailure(response) || !isSessionExpired?.()) return response;

        return NEVER_SETTLES;
      });
    };
  });
}

/**
 * Small tolerance so a browser clock slightly behind the server still
 * recognises a just-expired token.
 */
const EXPIRY_TOLERANCE_SECONDS = 60;

/**
 * Reads the `exp` claim straight off the stored JWT — earthnode signs (not
 * encrypts) dashboard tokens, so the payload is plain base64url JSON. A token
 * that can't be decoded or carries no numeric `exp` can never authenticate
 * again, so it counts as expired.
 */
export function isJwtExpired(token: string): boolean {
  try {
    const payload: unknown = parseJWT(token);

    if (typeof payload !== 'object' || payload === null || !('exp' in payload)) return true;

    const { exp } = payload;

    return typeof exp !== 'number' || exp <= Date.now() / 1000 + EXPIRY_TOLERANCE_SECONDS;
  } catch {
    return true;
  }
}

/**
 * `ErrorCode` is the only reliable signal — `ErrorMessage` is run through the
 * backend's translator, so it arrives in French for fr-CA users.
 */
function isAuthFailure(response: unknown): boolean {
  if (typeof response !== 'object' || response === null) return false;

  if (!('Success' in response) || !('ErrorCode' in response)) return false;

  return response.Success === false && response.ErrorCode == AUTH_FAILED_ERROR_CODE;
}
